import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import EntityStatusBadge from '#/modules/commonComponents/table/EntityStatusBadge';
import CustomerCommandBar from '#/modules/crmMgmt/customer/components/CustomerCommandBar';
import { CUSTOMER_ADDRESS_TYPE_OPTIONS } from '#/modules/crmMgmt/customer/helper/customerAddress';
import { formatCustomerStatusLabel } from '#/modules/crmMgmt/customer/helper/customerStatus';
import { formatDateTimeOrDash } from '#/utils/date';

function displayOrDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

function formatAddressTypeLabel(type) {
  const match = CUSTOMER_ADDRESS_TYPE_OPTIONS.find((option) => option.value === type);
  return match?.label ?? displayOrDash(type);
}

function HistoryField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">{value}</p>
    </div>
  );
}

HistoryField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

function formatAddressLine(row) {
  return [row?.postalCode, row?.address].filter(Boolean).join(' ') || '—';
}

function CustomerMainInfoCard({ detail, actions = null }) {
  const status = detail?.status;

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {displayOrDash(detail?.code)}
              </h1>
              {status ? (
                <EntityStatusBadge status={status} label={formatCustomerStatusLabel(status)} />
              ) : null}
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {displayOrDash(detail?.name)}
            </p>
          </div>
          {actions ? (
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {actions}
            </div>
          ) : null}
        </div>

        <div
          className={cn(
            'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
            'sm:grid-cols-2 md:grid-cols-4',
          )}
        >
          <HistoryField
            label="建立"
            value={formatDateTimeOrDash(
              detail?.record?.createdAt ?? detail?.createdAt,
            )}
          />
          <HistoryField
            label="修改"
            value={formatDateTimeOrDash(
              detail?.record?.updatedAt ?? detail?.updatedAt,
            )}
          />
          <HistoryField label="中文名" value={displayOrDash(detail?.chineseName)} />
          <HistoryField label="英文名" value={displayOrDash(detail?.englishName)} />
          <HistoryField label="統編" value={displayOrDash(detail?.taxIdNumber)} />
          <HistoryField label="發票抬頭" value={displayOrDash(detail?.invoiceTitle)} />
          <HistoryField label="兩碼 SN 前綴" value={displayOrDash(detail?.snCode2)} />
          <HistoryField label="三碼 SN 前綴" value={displayOrDash(detail?.snCode3)} />
          <HistoryField label="稅籍國碼" value={displayOrDash(detail?.taxCountryCode)} />
          <HistoryField label="電話" value={displayOrDash(detail?.phone)} />
          <HistoryField label="傳真" value={displayOrDash(detail?.fax)} />
          <HistoryField label="網址" value={displayOrDash(detail?.homepage)} />
          <div className="sm:col-span-2 md:col-span-4">
            <p className="text-xs text-gray-500">備註</p>
            <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
              {displayOrDash(detail?.remark)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

CustomerMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  actions: PropTypes.node,
};

function CustomerAddressesReadonlySection({ addresses = [] }) {
  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <h2 className="form-block__title">地址</h2>
        {addresses.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">尚無地址</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {addresses.map((row, index) => (
              <li
                key={String(row?.id ?? `address-${index}`)}
                className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
              >
                <p className="font-medium text-gray-900">
                  {formatAddressTypeLabel(row?.addressType)}
                  {row?.isDefault ? (
                    <span className="ml-2 text-xs font-normal text-emerald-700">預設</span>
                  ) : null}
                </p>
                <p className="mt-1 text-gray-800">{formatAddressLine(row)}</p>
                {row?.remark ? (
                  <p className="mt-0.5 text-xs text-gray-500">
                    備註：
                    {row.remark}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

CustomerAddressesReadonlySection.propTypes = {
  addresses: PropTypes.arrayOf(PropTypes.object),
};

function CustomerContactsReadonlySection({ contacts = [] }) {
  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <h2 className="form-block__title">聯絡人</h2>
        {contacts.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">尚無聯絡人</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[36rem] text-left text-sm">
              <thead className="bg-slate-100 text-xs text-gray-600">
                <tr>
                  <th className="px-2 py-1.5 font-medium">姓名</th>
                  <th className="px-2 py-1.5 font-medium">部門</th>
                  <th className="px-2 py-1.5 font-medium">職稱</th>
                  <th className="px-2 py-1.5 font-medium">電話</th>
                  <th className="px-2 py-1.5 font-medium">手機</th>
                  <th className="px-2 py-1.5 font-medium">Email</th>
                  <th className="px-2 py-1.5 font-medium">預設</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((row, index) => (
                  <tr
                    key={String(row?.id ?? `contact-${index}`)}
                    className="border-t border-slate-200"
                  >
                    <td className="px-2 py-1.5">{displayOrDash(row?.name)}</td>
                    <td className="px-2 py-1.5">{displayOrDash(row?.department)}</td>
                    <td className="px-2 py-1.5">{displayOrDash(row?.title)}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      {displayOrDash(row?.phone)}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      {displayOrDash(row?.mobile)}
                    </td>
                    <td className="px-2 py-1.5">{displayOrDash(row?.email)}</td>
                    <td className="px-2 py-1.5">{row?.isDefault ? '是' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

CustomerContactsReadonlySection.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.object),
};

const CustomerBlock = ({ detail, onRefetch, showActions = false }) => (
  <div className="space-y-4">
    <CustomerMainInfoCard
      detail={detail}
      actions={
        showActions ? (
          <CustomerCommandBar
            key={`${detail.id}-${detail.record?.updatedAt ?? detail.updatedAt ?? ''}`}
            detail={detail}
            onRefetch={onRefetch}
          />
        ) : null
      }
    />
    <CustomerAddressesReadonlySection addresses={detail?.addresses ?? []} />
    <CustomerContactsReadonlySection contacts={detail?.contacts ?? []} />
  </div>
);

CustomerBlock.propTypes = {
  detail: PropTypes.object.isRequired,
  onRefetch: PropTypes.func,
  showActions: PropTypes.bool,
};

export default CustomerBlock;
